import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCompanies } from "../../hooks/useCompanies";
import { useChannelAiMapping } from "../../hooks/useChannelAiMapping";
import { channelCardsForSegment, findCardByTipo, renderChannelCatalogIcon } from "../../constants/channelCatalog";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";

const btnSx = {
  textTransform: "none",
  borderRadius: 2,
  fontWeight: 600,
  px: 2,
};

function formatPhone(raw) {
  const d = String(raw || "").replace(/\D+/g, "");
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) {
    const ddd = d.slice(2, 4);
    const rest = d.slice(4);
    if (rest.length === 9) return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    if (rest.length === 8) return `+55 ${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  if (d.length === 10 || d.length === 11) {
    const ddd = d.slice(0, 2);
    const rest = d.slice(2);
    if (rest.length === 9) return `${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    if (rest.length === 8) return `${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  return String(raw || "").trim();
}

function looksLikePhone(raw) {
  const d = String(raw || "").replace(/\D+/g, "");
  if (d.length === 10 || d.length === 11) return true;
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) return true;
  return d.length >= 10 && d.length <= 15;
}

function isSyntheticLabel(value) {
  const s = String(value || "").trim();
  return !s || /^channel:/i.test(s) || /^widget:/i.test(s) || /^xpro:\d+$/i.test(s) || /^\d{1,6}$/.test(s);
}

function formatIdentity(row) {
  const id = row?.identity || {};
  if (id.phone && looksLikePhone(id.phone)) return formatPhone(id.phone);
  if (id.email) return id.email;
  if (id.handle) return id.handle.startsWith("@") ? id.handle : `@${id.handle}`;
  if (id.slug) return id.slug;
  if (id.name && !isSyntheticLabel(id.name)) return id.name;
  if (id.rating != null && Number.isFinite(Number(id.rating))) {
    const stars = Number(id.rating).toFixed(1);
    const count = Number(id.userRatingsTotal);
    return Number.isFinite(count) && count > 0 ? `${stars} ★ (${count})` : `${stars} ★`;
  }
  if (id.url && /^https?:/i.test(String(id.url))) return String(id.url).replace(/^https?:\/\/(www\.)?/i, "");
  const label = String(row?.displayLabel || "").trim();
  if (label && !isSyntheticLabel(label)) return label;
  return "";
}

function isHttpUrl(raw) {
  return /^https?:\/\//i.test(String(raw || "").trim());
}

function isOpenableUrl(raw) {
  return /^(https?:\/\/|tel:)/i.test(String(raw || "").trim());
}

function identityHref(row) {
  const id = row?.identity || {};
  if (isOpenableUrl(id.url)) return String(id.url).trim();
  const evidenceUrl = row?.evidence?.reputation?.url || row?.evidence?.googleReviews?.mapsUrl;
  return isHttpUrl(evidenceUrl) ? String(evidenceUrl).trim() : "";
}

function fallbackGoogleSearch(row) {
  const label = formatIdentity(row);
  return label ? `https://www.google.com/search?q=${encodeURIComponent(label)}` : "";
}

function uniqueSourceItems(sources, fallbackQuery) {
  const seen = new Set();
  const out = [];
  for (const source of sources || []) {
    const url = isOpenableUrl(source.url)
      ? String(source.url).trim()
      : source.kind === "google"
        ? `https://www.google.com/search?q=${encodeURIComponent(fallbackQuery || "")}`
        : "";
    const key = `${source.kind}|${url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind: source.kind, url });
  }
  return out;
}

function qualityTone(label) {
  if (label === "excellent" || label === "good") return "success";
  if (label === "fair") return "warning";
  if (label === "poor") return "error";
  return "default";
}

function visibilityTone(label) {
  if (label === "dominant" || label === "high") return "info";
  if (label === "medium") return "warning";
  if (label === "hidden" || label === "low") return "warning";
  return "default";
}

function draftFromInstance(idProjeto, row) {
  const identity = row?.identity || {};
  const tipo = Number(row?.recommendedTipo || row?.tipo);
  const draft = {
    tipo,
    id_projeto: idProjeto,
    nome: row?.displayLabel || "",
  };
  if (identity.phone) {
    draft.numerowhatsapp = identity.phone;
    draft.twilio_phone_number = identity.phone;
  }
  if (identity.email) {
    draft.email = identity.email;
    draft.imapemail = identity.email;
  }
  if (identity.slug) draft.ra_company_slug = identity.slug;
  if (identity.url && tipo === 40) draft.ecom_store_url = identity.url;
  return draft;
}

const GOOGLE_REVIEWS_TIPO = 48;

function groupRows(rows, segment, t) {
  const byTipo = new Map();
  for (const row of rows || []) {
    const tipo = Number(row.tipo);
    if (!byTipo.has(tipo)) byTipo.set(tipo, []);
    byTipo.get(tipo).push(row);
  }
  const cards = [
    ...channelCardsForSegment(segment).filter((c) => c.id !== 0),
    {
      id: GOOGLE_REVIEWS_TIPO,
      key: "google_reviews",
      label: t("channelsPage.aiMapping.googleReviewsCard"),
      description: t("channelsPage.aiMapping.channelHelp.google_reviews.explain"),
      color: "#4285F4",
    },
  ];
  return cards.map((card) => ({
    card,
    instances: (byTipo.get(card.id) || []).filter((r) => r.instanceKey && r.instanceKey !== "_none"),
  }));
}

function cardIcon(card, iconProps = {}) {
  if (Number(card.id) === GOOGLE_REVIEWS_TIPO) {
    return <StarRoundedIcon fontSize="small" sx={{ color: card.color || "#4285F4", ...(iconProps.sx || {}) }} />;
  }
  return renderChannelCatalogIcon(card.id, iconProps);
}

function channelHelpText(t, card) {
  const key = card?.key || "";
  const explainKey = `channelsPage.aiMapping.channelHelp.${key}.explain`;
  const benefitKey = `channelsPage.aiMapping.channelHelp.${key}.benefit`;
  const explain = t(explainKey);
  const benefit = t(benefitKey);
  return {
    explain: explain === explainKey ? String(card?.description || "") : explain,
    benefit: benefit === benefitKey ? "" : benefit,
  };
}

function sourceLabel(t, kind) {
  const key = `channelsPage.aiMapping.source.${kind}`;
  const translated = t(key);
  return translated === key ? kind : translated;
}

function ScoreBlock({ caption, label, hint, tone, icon }) {
  return (
    <Tooltip title={hint || ""}>
      <Stack spacing={0.25} sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          {caption}
        </Typography>
        <Chip
          size="small"
          color={tone === "default" ? "default" : tone}
          variant={tone === "default" ? "outlined" : "filled"}
          icon={icon}
          label={label}
          sx={{ fontWeight: 600, height: 24, "& .MuiChip-icon": { fontSize: 16 } }}
        />
      </Stack>
    </Tooltip>
  );
}

function tipFieldIcon(field) {
  if (field === "site") return <LanguageOutlinedIcon sx={{ fontSize: 18 }} />;
  if (field === "support_whatsapp") return <WhatsAppIcon sx={{ fontSize: 18 }} />;
  if (field === "telefone") return <PhoneOutlinedIcon sx={{ fontSize: 18 }} />;
  if (field === "email") return <EmailOutlinedIcon sx={{ fontSize: 18 }} />;
  return <LinkOutlinedIcon sx={{ fontSize: 18 }} />;
}

function displayTipValue(tip) {
  if (tip.field === "telefone" || tip.field === "support_whatsapp") return formatPhone(tip.value);
  if (isHttpUrl(tip.value)) return String(tip.value).replace(/^https?:\/\/(www\.)?/i, "");
  return String(tip.value || "").trim();
}

function tipHref(tip) {
  if (tip.field === "email" && tip.value) return `mailto:${String(tip.value).trim()}`;
  if ((tip.field === "telefone" || tip.field === "support_whatsapp") && looksLikePhone(tip.value)) {
    const digits = String(tip.value).replace(/\D+/g, "");
    return digits ? `tel:+${digits}` : "";
  }
  return isHttpUrl(tip.value) ? String(tip.value).trim() : "";
}

function CadastroTips({ tips, t, onEditCompany, onApply, applying }) {
  if (!Array.isArray(tips) || !tips.length) return null;
  const applyable = tips.filter((tip) => tip.field && tip.value);
  const others = tips.filter((tip) => !(tip.field && tip.value));
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        borderColor: "info.light",
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "flex-start" }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <LightbulbOutlinedIcon color="info" sx={{ mt: 0.15 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t("channelsPage.aiMapping.tipsTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
              {applyable.length
                ? t("channelsPage.aiMapping.tipsFoundIntro", { count: applyable.length })
                : t("channelsPage.aiMapping.tipsIntro")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          {applyable.length > 1 ? (
            <Button
              size="small"
              variant="contained"
              disabled={Boolean(applying)}
              onClick={() => onApply?.(applyable)}
              sx={btnSx}
            >
              {applying === "all"
                ? t("channelsPage.aiMapping.tipsApplying")
                : t("channelsPage.aiMapping.tipsApplyAll", { count: applyable.length })}
            </Button>
          ) : null}
          {onEditCompany ? (
            <Button size="small" variant="text" onClick={onEditCompany} sx={btnSx}>
              {t("channelsPage.aiMapping.tipsEditCompany")}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {applyable.length ? (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {applyable.map((tip) => {
            const href = tipHref(tip);
            const value = displayTipValue(tip);
            const fieldKey = `channelsPage.aiMapping.tipsField.${tip.field}`;
            const fieldLabel = t(fieldKey);
            return (
              <Paper
                key={`${tip.code}-${tip.field}`}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "background.paper" }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                        color: "info.dark",
                      }}
                    >
                      {tipFieldIcon(tip.field)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                        {fieldLabel === fieldKey ? tip.field : fieldLabel}
                      </Typography>
                      {href ? (
                        <Link
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontWeight: 600,
                            wordBreak: "break-all",
                            lineHeight: 1.4,
                          }}
                        >
                          {value}
                          <OpenInNewRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                          {value}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={Boolean(applying)}
                    onClick={() => onApply?.([tip])}
                    sx={{ ...btnSx, alignSelf: { xs: "stretch", sm: "center" }, minWidth: 96 }}
                  >
                    {applying === tip.field
                      ? t("channelsPage.aiMapping.tipsApplying")
                      : t("channelsPage.aiMapping.tipsApplyField")}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : null}

      {others.length ? (
        <Box sx={{ mt: applyable.length ? 2 : 1.5 }}>
          {applyable.length ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.3 }}>
                {t("channelsPage.aiMapping.tipsOtherTitle")}
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
            </>
          ) : null}
          <Stack spacing={0.75}>
            {others.map((tip) => (
              <Typography key={tip.code} variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {t(`channelsPage.aiMapping.tips.${tip.code}`, tip.params || {})}
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Paper>
  );
}

function AnalysisFacts({ row, t }) {
  const ra = row?.evidence?.reputation;
  const google = row?.evidence?.googleReviews;
  const facts = [];
  if (ra && (ra.score != null || ra.totalComplaints != null || ra.responseRate != null || ra.status || ra.avgResponseTime)) {
    if (ra.score != null) facts.push(t("channelsPage.aiMapping.raScore", { score: ra.score }));
    if (ra.totalComplaints != null) facts.push(t("channelsPage.aiMapping.raComplaints", { count: ra.totalComplaints }));
    if (ra.responseRate != null) facts.push(t("channelsPage.aiMapping.raResponseRate", { rate: ra.responseRate }));
    if (ra.avgResponseTime) facts.push(t("channelsPage.aiMapping.raAvgTime", { time: ra.avgResponseTime }));
    if (ra.status) facts.push(t("channelsPage.aiMapping.raStatus", { status: ra.status }));
    if (ra.wouldRecommend != null) facts.push(t("channelsPage.aiMapping.raRecommend", { rate: ra.wouldRecommend }));
  }
  if (google && (google.rating != null || google.userRatingsTotal != null)) {
    if (google.rating != null) facts.push(t("channelsPage.aiMapping.googleRating", { score: google.rating }));
    if (google.userRatingsTotal != null) {
      facts.push(t("channelsPage.aiMapping.googleReviewCount", { count: google.userRatingsTotal }));
    }
  }
  const snippet = google?.snippet || row?.evidence?.snippet || "";
  if (!facts.length && !snippet) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {facts.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {facts.join(" · ")}
        </Typography>
      ) : null}
      {snippet ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
          {snippet}
        </Typography>
      ) : null}
    </Box>
  );
}

function MissingChannelCard({ card, t, onCreate }) {
  const help = channelHelpText(t, card);
  const canCreate = Boolean(findCardByTipo(card.id));
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "action.hover",
        borderColor: "warning.light",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ mt: 0.25, opacity: 0.7 }}>{cardIcon(card, { fontSize: "small" })}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {card.label}
            </Typography>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<SearchOffOutlinedIcon />}
              label={t("channelsPage.aiMapping.notFound")}
              sx={{ fontWeight: 600, height: 24 }}
            />
          </Stack>
          {help.explain ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
              {help.explain}
            </Typography>
          ) : null}
          {help.benefit ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, lineHeight: 1.45 }}>
              {help.benefit}
            </Typography>
          ) : null}
          {canCreate ? (
            <Button size="small" variant="outlined" onClick={onCreate} sx={{ ...btnSx, mt: 1 }}>
              {t("channelsPage.aiMapping.createChannel")}
            </Button>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function InstanceCard({ row, t, onCreate }) {
  const inXpro = row.presence === "configured" || row.presence === "configured_and_detected";
  const detected = row.presence === "detected" || row.presence === "configured_and_detected";
  const hidden = row.visibilityLabel === "hidden";
  const sources = uniqueSourceItems(row.sources, fallbackGoogleSearch(row));
  const canCreate = row.recommendation === "create" && findCardByTipo(row.recommendedTipo || row.tipo);
  const href = identityHref(row);
  const label = formatIdentity(row);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "flex-start" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {href ? (
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 700, wordBreak: "break-word", display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {label}
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>
          ) : (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
              {label}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            {inXpro ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
                label={t("channelsPage.aiMapping.inXpro")}
                sx={{ fontWeight: 600 }}
              />
            ) : null}
            {detected ? (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                icon={<PublicOutlinedIcon />}
                label={t("channelsPage.aiMapping.detected")}
                sx={{ fontWeight: 600 }}
              />
            ) : null}
            {hidden ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<VisibilityOffOutlinedIcon />}
                label={t("channelsPage.aiMapping.hiddenShort")}
                sx={{ fontWeight: 600 }}
              />
            ) : null}
          </Stack>
          <AnalysisFacts row={row} t={t} />
          {hidden ? (
            <Typography variant="caption" color="warning.dark" sx={{ display: "block", mt: 0.75, fontStyle: "italic" }}>
              {t("channelsPage.aiMapping.visibilityHiddenHint")}
            </Typography>
          ) : null}
          {sources.length ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {t("channelsPage.aiMapping.sources")}:
              </Typography>
              {sources.map((source) =>
                source.url ? (
                  <Link
                    key={`${source.kind}-${source.url}`}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    variant="caption"
                    sx={{ fontStyle: "italic", display: "inline-flex", alignItems: "center", gap: 0.25 }}
                  >
                    {sourceLabel(t, source.kind)}
                    <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
                  </Link>
                ) : (
                  <Typography key={source.kind} variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {sourceLabel(t, source.kind)}
                  </Typography>
                )
              )}
            </Stack>
          ) : null}
        </Box>
        <Stack direction={{ xs: "row", sm: "column" }} spacing={1.25} alignItems={{ sm: "flex-end" }}>
          <Stack direction="row" spacing={2}>
            <ScoreBlock
              caption={t("channelsPage.aiMapping.qualityCaption")}
              label={t(`channelsPage.aiMapping.qualityLabel.${row.qualityLabel || "none"}`)}
              hint={t(`channelsPage.aiMapping.qualityHint.${row.qualityLabel || "none"}`)}
              tone={qualityTone(row.qualityLabel)}
              icon={<StarRoundedIcon />}
            />
            <ScoreBlock
              caption={t("channelsPage.aiMapping.visibilityCaption")}
              label={t(`channelsPage.aiMapping.visibilityLabel.${row.visibilityLabel || "hidden"}`)}
              hint={
                hidden
                  ? t("channelsPage.aiMapping.visibilityHiddenHint")
                  : t("channelsPage.aiMapping.visibilityGoogleHint")
              }
              tone={visibilityTone(row.visibilityLabel)}
              icon={hidden ? <VisibilityOffOutlinedIcon /> : <PublicOutlinedIcon />}
            />
          </Stack>
          {canCreate ? (
            <Button size="small" variant="contained" onClick={onCreate} sx={btnSx}>
              {t("channelsPage.aiMapping.createChannel")}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function MapChannelsAiDialog({ open, onClose, onCreateChannel }) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { segment } = useWorkspaceSegment();
  const mappingApi = useChannelAiMapping();
  const { data, isLoading, updateMutation } = useCompanies(1, 1000, "");
  const companies = data?.companies || [];

  const [selected, setSelected] = useState(() => new Set());
  const [phase, setPhase] = useState("pick");
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [applying, setApplying] = useState("");
  const [applyNotice, setApplyNotice] = useState("");

  useEffect(() => {
    if (!open) {
      setPhase("pick");
      setJob(null);
      setError("");
      setStarting(false);
      setApplying("");
      setApplyNotice("");
      return undefined;
    }
    let stop = false;
    (async () => {
      try {
        const active = await mappingApi.getActive();
        if (stop) return;
        if (active?.active && active.job) {
          setJob(active.job);
          setPhase("run");
        }
      } catch {
        /* sem job ativo */
      }
    })();
    return () => {
      stop = true;
    };
  }, [open, mappingApi]);

  useEffect(() => {
    if (!open || !job?.jobId) return undefined;
    const busy = job.status === "queued" || job.status === "running";
    if (!busy) {
      if (job.status === "completed") setPhase("result");
      if (job.status === "failed" || job.status === "cancelled") {
        setPhase(Array.isArray(job.companies) && job.companies.length ? "result" : "pick");
      }
      return undefined;
    }
    setPhase("run");
    let stop = false;
    const tick = async () => {
      try {
        const next = await mappingApi.getJob(job.jobId);
        if (stop || !next) return;
        setJob(next);
        if (next.status === "completed") setPhase("result");
        if (next.status === "failed" || next.status === "cancelled") {
          setError(next.errorMessage || t("channelsPage.aiMapping.jobFailed"));
          setPhase(Array.isArray(next.companies) && next.companies.length ? "result" : "pick");
        }
      } catch (err) {
        if (!stop) setError(err?.message || t("channelsPage.aiMapping.jobFailed"));
      }
    };
    const id = setInterval(tick, 2000);
    tick();
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [open, job?.jobId, job?.status, t, mappingApi]);

  const allSelected = companies.length > 0 && selected.size === companies.length;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(companies.map((c) => Number(c.id))));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const start = async () => {
    if (!selected.size) return;
    setStarting(true);
    setError("");
    try {
      const res = await mappingApi.start({ ids_projetos: [...selected] });
      const busy = res?.status === "queued" || res?.status === "running";
      if (res?.jobId && !busy) {
        const full = await mappingApi.getJob(res.jobId).catch(() => res);
        setJob(full || res);
        const fullBusy = full?.status === "queued" || full?.status === "running";
        setPhase(fullBusy ? "run" : Array.isArray(full?.companies) && full.companies.length ? "result" : "pick");
      } else {
        setJob(res);
        setPhase("run");
      }
    } catch (err) {
      setError(err?.message || t("channelsPage.aiMapping.startError"));
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!job?.jobId) return;
    try {
      await mappingApi.cancel(job.jobId);
      setJob((cur) => (cur ? { ...cur, status: "cancelled" } : cur));
      setPhase(Array.isArray(job.companies) && job.companies.length ? "result" : "pick");
    } catch (err) {
      setError(err?.message || t("channelsPage.aiMapping.jobFailed"));
    }
  };

  const loadLatest = async () => {
    if (!selected.size) return;
    setError("");
    try {
      const res = await mappingApi.getLatest([...selected]);
      const items = res?.items || [];
      if (!items.length) {
        setError(t("channelsPage.aiMapping.noLatest"));
        return;
      }
      const tipsByProjeto = res?.tipsByProjeto || {};
      const byCompany = new Map();
      for (const row of items) {
        const id = Number(row.idProjeto);
        if (!byCompany.has(id)) {
          const company = companies.find((c) => Number(c.id) === id);
          byCompany.set(id, {
            idProjeto: id,
            nome: company?.nome || `#${id}`,
            tips: tipsByProjeto[id] || tipsByProjeto[String(id)] || [],
            rows: [],
          });
        }
        byCompany.get(id).rows.push(row);
      }
      setJob({
        jobId: "latest",
        status: "completed",
        companies: [...byCompany.values()],
        processed: byCompany.size,
        total: byCompany.size,
      });
      setPhase("result");
    } catch (err) {
      setError(err?.message || t("channelsPage.aiMapping.jobFailed"));
    }
  };

  const applyCadastro = async (idProjeto, tipsToApply) => {
    const list = (Array.isArray(tipsToApply) ? tipsToApply : []).filter((tip) => tip.field && tip.value);
    if (!idProjeto || !list.length) return;
    const projeto = {};
    for (const tip of list) projeto[tip.field] = tip.value;
    const key = list.length > 1 ? "all" : list[0].field;
    setApplying(key);
    setApplyNotice("");
    setError("");
    try {
      await updateMutation.mutateAsync({ id: idProjeto, projeto });
      setJob((cur) => {
        if (!cur?.companies) return cur;
        return {
          ...cur,
          companies: cur.companies.map((company) =>
            Number(company.idProjeto) === Number(idProjeto)
              ? {
                  ...company,
                  tips: (company.tips || []).filter(
                    (tip) => !list.some((applied) => applied.field && applied.field === tip.field)
                  ),
                }
              : company
          ),
        };
      });
      setApplyNotice(
        list.length > 1
          ? t("channelsPage.aiMapping.tipsApplyAllDone", { count: list.length })
          : t("channelsPage.aiMapping.tipsApplyDone")
      );
    } catch (err) {
      setError(err?.message || t("channelsPage.aiMapping.tipsApplyError"));
    } finally {
      setApplying("");
    }
  };

  const progress = Number(job?.overallProgress) || 0;
  const resultCompanies = useMemo(() => job?.companies || [], [job]);
  const subtitle =
    phase === "run"
      ? t("channelsPage.aiMapping.subtitleRun")
      : phase === "result"
        ? t("channelsPage.aiMapping.subtitleResult")
        : t("channelsPage.aiMapping.subtitlePick");

  return (
    <Dialog
      open={open}
      onClose={starting ? undefined : onClose}
      fullWidth
      maxWidth={phase === "result" ? "lg" : "md"}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
            <AutoAwesomeRoundedIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {t("channelsPage.aiMapping.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {applyNotice ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setApplyNotice("")}>
            {applyNotice}
          </Alert>
        ) : null}

        {phase === "pick" ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {t("channelsPage.aiMapping.intro")}
            </Typography>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button size="small" onClick={toggleAll} sx={btnSx}>
                    {allSelected ? t("channelsPage.aiMapping.unselectAll") : t("channelsPage.aiMapping.selectAll")}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {t("channelsPage.aiMapping.selectCount", { count: selected.size })}
                  </Typography>
                </Stack>
                {companies.map((company) => {
                  const id = Number(company.id);
                  const checked = selected.has(id);
                  return (
                    <Paper
                      key={id}
                      variant="outlined"
                      onClick={() => toggleOne(id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        cursor: "pointer",
                        borderColor: checked ? "primary.main" : "divider",
                        bgcolor: checked ? "action.selected" : "background.paper",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Checkbox checked={checked} tabIndex={-1} />
                        <Avatar sx={{ bgcolor: "grey.100", color: "text.secondary", width: 36, height: 36 }}>
                          <BusinessOutlinedIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {company.nome}
                          </Typography>
                          {company.site ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <LanguageOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {String(company.site).replace(/^https?:\/\/(www\.)?/i, "")}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                              {t("channelsPage.aiMapping.noSite")}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </>
        ) : null}

        {phase === "run" ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <CircularProgress size={32} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t("channelsPage.aiMapping.progress", {
                    processed: job?.processed || 0,
                    total: job?.total || 0,
                  })}
                </Typography>
                {job?.currentCompanyName ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {job.currentCompanyName}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ width: "100%" }}>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 999 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  {progress}%
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : null}

        {phase === "result" ? (
          <Stack spacing={2.5}>
            {resultCompanies.map((company) => {
              const groups = groupRows(company.rows, segment, t);
              const found = groups.filter((g) => g.instances.length);
              const missing = groups.filter((g) => !g.instances.length);
              const allRows = found.flatMap((g) => g.instances);
              const inXpro = allRows.filter((r) => r.presence === "configured" || r.presence === "configured_and_detected").length;
              const toCreate = allRows.filter((r) => r.recommendation === "create").length;
              return (
                <Box key={company.idProjeto}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <BusinessOutlinedIcon fontSize="small" color="action" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {company.nome || `#${company.idProjeto}`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={t("channelsPage.aiMapping.summaryFound", { count: allRows.length })} />
                      <Chip size="small" color="success" variant="outlined" label={t("channelsPage.aiMapping.summaryInXpro", { count: inXpro })} />
                      <Chip size="small" color="primary" variant="outlined" label={t("channelsPage.aiMapping.summaryToCreate", { count: toCreate })} />
                      {missing.length ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          icon={<SearchOffOutlinedIcon />}
                          label={t("channelsPage.aiMapping.summaryMissing", { count: missing.length })}
                        />
                      ) : null}
                    </Stack>
                  </Paper>
                  {company.error ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {company.error}
                    </Alert>
                  ) : null}
                  <CadastroTips
                    tips={company.tips}
                    t={t}
                    applying={applying}
                    onApply={(tipsToApply) => applyCadastro(company.idProjeto, tipsToApply)}
                    onEditCompany={() => {
                      onClose?.();
                      navigate("/settings/companies");
                    }}
                  />
                  {found.length ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                      <PublicOutlinedIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t("channelsPage.aiMapping.foundTitle")}
                      </Typography>
                      <Chip size="small" variant="outlined" label={allRows.length} />
                    </Stack>
                  ) : null}
                  <Stack spacing={2}>
                    {found.map(({ card, instances }) => (
                      <Box key={card.id}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          {cardIcon(card, { fontSize: "small" })}
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {card.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("channelsPage.aiMapping.instanceCount", { count: instances.length })}
                          </Typography>
                        </Stack>
                        <Stack spacing={1}>
                          {instances.map((row) => (
                            <InstanceCard
                              key={`${row.tipo}-${row.instanceKey}`}
                              row={row}
                              t={t}
                              onCreate={() => {
                                onCreateChannel?.(draftFromInstance(company.idProjeto, row));
                                onClose?.();
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                  {missing.length ? (
                    <Box sx={{ mt: 2.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <SearchOffOutlinedIcon fontSize="small" color="warning" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {t("channelsPage.aiMapping.missingTitle")}
                        </Typography>
                        <Chip size="small" color="warning" variant="outlined" label={missing.length} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        {t("channelsPage.aiMapping.missingIntro")}
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                          gap: 1,
                        }}
                      >
                        {missing.map(({ card }) => (
                          <MissingChannelCard
                            key={card.id}
                            card={card}
                            t={t}
                            onCreate={() => {
                              onCreateChannel?.(
                                draftFromInstance(company.idProjeto, {
                                  tipo: card.id,
                                  recommendedTipo: card.id,
                                  displayLabel: card.label,
                                  identity: {},
                                })
                              );
                              onClose?.();
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {phase === "run" ? (
          <Button onClick={cancel} sx={btnSx}>
            {t("channelsPage.aiMapping.cancel")}
          </Button>
        ) : (
          <Button onClick={phase === "result" ? () => setPhase("pick") : onClose} sx={btnSx}>
            {phase === "result" ? t("channelsPage.aiMapping.mapAgain") : t("channelsPage.aiMapping.close")}
          </Button>
        )}
        {phase === "pick" ? (
          <>
            <Button onClick={loadLatest} disabled={!selected.size} sx={btnSx}>
              {t("channelsPage.aiMapping.openLatest")}
            </Button>
            <Button variant="contained" onClick={start} disabled={!selected.size || starting} sx={btnSx}>
              {starting ? t("channelsPage.aiMapping.starting") : t("channelsPage.aiMapping.map")}
            </Button>
          </>
        ) : null}
        {phase === "result" ? (
          <Button variant="contained" onClick={onClose} sx={btnSx}>
            {t("channelsPage.aiMapping.close")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
